/**
 * Which plan cards the accountant client-onboarding wizard may show.
 *
 * `/tenants/current/plans` returns every tier unfiltered, so filtering is the
 * portal's job — but it has to be the SERVER's rule, not a second one invented
 * per surface. `eligiblePlanIds` comes off `/tenants/current`
 * (`resolveEligiblePlanIds`: régimen + company type + benefit flow) and is the
 * same list PlanStep, ChangePlanModal, GeneratePaymentLinkDialog and both
 * mobile plan screens filter by.
 *
 * Extracted from accountant-client-onboarding-page.tsx for the same reason
 * carteraSearch.ts and tenantAction.ts were: the rule is a judgement call with
 * a non-obvious asymmetry, and inside a 3.5k-line component no test could
 * reach it.
 *
 * The wizard previously gated only `literalE`, so `semilla` was offered to
 * clients `resolveEligiblePlanIds` withholds it from — Literal E clients on any
 * benefit flow, and any companyType that is not unipersonal/SAS — and
 * PlanCheckoutService then refused it with "El plan Semilla es solo para
 * unipersonales o SAS en régimen general".
 */

/** The minimum shape needed: these are `/tenants/current/plans` rows. */
export interface PlanCandidate {
  id: string;
}

/**
 * @param plans      every tier from `/tenants/current/plans`
 * @param eligiblePlanIds  server-computed list from `/tenants/current`;
 *                   `null` means not loaded yet → show all, matching PlanStep
 * @param literalEDeclared the accountant's live in-session Literal E answer
 */
export function selectVisiblePlans<T extends PlanCandidate>(
  plans: readonly T[],
  eligiblePlanIds: readonly string[] | null,
  literalEDeclared: boolean,
): T[] {
  return plans.filter((p) => {
    // literalE keeps its own rule DELIBERATELY — do not fold it into the
    // eligiblePlanIds check. `literalEDeclared` is the accountant's in-session
    // Sí/No, persisted to company.literalE (which flips literalEVerified) only
    // later, at activation. Until that write the server list does NOT contain
    // literalE, so deferring to it would hide the card the accountant just
    // asked for. Collapsing the two branches breaks the Literal E flow in a way
    // the type checker cannot catch.
    if (p.id === "literalE") return literalEDeclared;
    return eligiblePlanIds == null || eligiblePlanIds.includes(p.id);
  });
}
